import { useState, useEffect } from 'react';
import {
    Button,
    CardActions,
    CardContent,
    CircularProgress,
    Typography,
    TextField,
    Box,
    Alert,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { AuthPageLayout, LogoHeader } from '../login/components';
import { supabase } from '../providers/supabase/supabase';
import { getVerifiedFactors, verifyTotp } from '../providers/supabase/mfa';

export const MfaVerifyPage = () => {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [factorId, setFactorId] = useState<string | null>(null);
    const [initLoading, setInitLoading] = useState(true);
    const [error, setError] = useState('');
    const notify = useNotify();
    const navigate = useNavigate();

    useEffect(() => {
        const loadFactor = async () => {
            try {
                const factors = await getVerifiedFactors();
                if (factors.length === 0) {
                    // No verified factors — redirect to enrollment
                    navigate('/mfa-enroll', { replace: true });
                    return;
                }
                setFactorId(factors[0].id);
            } catch (err: any) {
                console.error('Failed to load MFA factors:', err);
                setError('Failed to load MFA status.');
            } finally {
                setInitLoading(false);
            }
        };
        loadFactor();
    }, [navigate]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!code || !factorId) return;

        setLoading(true);
        setError('');

        try {
            await verifyTotp(factorId, code);
            navigate('/', { replace: true });
        } catch (err: any) {
            console.error('MFA verification failed:', err);
            setError('Invalid code. Please try again.');
            setCode('');
        } finally {
            setLoading(false);
        }
    };

    const handleSignOut = async () => {
        await supabase.auth.signOut();
        navigate('/login', { replace: true });
    };

    if (initLoading) {
        return (
            <AuthPageLayout centered maxWidth={400}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 4,
                    }}
                >
                    <CircularProgress />
                </Box>
            </AuthPageLayout>
        );
    }

    return (
        <AuthPageLayout maxWidth={400}>
            <Title title="Two-Factor Authentication" />
            <LogoHeader />

            <form onSubmit={handleSubmit}>
                <CardContent sx={{ width: 300 }}>
                    <Typography
                        variant="h6"
                        component="h1"
                        gutterBottom
                        align="center"
                        sx={{ mb: 1 }}
                    >
                        Two-Factor Authentication
                    </Typography>
                    <Typography
                        variant="body2"
                        align="center"
                        sx={{ mb: 3, color: 'text.secondary' }}
                    >
                        Enter the 6-digit code from your authenticator app.
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="Verification Code"
                        value={code}
                        onChange={e => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val.length <= 6) setCode(val);
                        }}
                        inputProps={{
                            maxLength: 6,
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            style: {
                                textAlign: 'center',
                                fontSize: '1.5rem',
                                letterSpacing: '0.5rem',
                            },
                        }}
                        autoFocus
                        autoComplete="one-time-code"
                        disabled={loading}
                        variant="outlined"
                        sx={{ mb: 2 }}
                    />
                </CardContent>

                <CardActions
                    sx={{
                        flexDirection: 'column',
                        gap: 1,
                        px: 2,
                        pb: 2,
                    }}
                >
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading || code.length !== 6}
                        fullWidth
                        sx={{ mt: 1 }}
                    >
                        {loading ? (
                            <CircularProgress
                                size={19}
                                thickness={3}
                                sx={{ margin: 0.3 }}
                            />
                        ) : (
                            'Verify'
                        )}
                    </Button>

                    <Button
                        size="small"
                        onClick={handleSignOut}
                        disabled={loading}
                        sx={{
                            mt: 1,
                            color: 'text.secondary',
                            textTransform: 'none',
                        }}
                    >
                        Sign out
                    </Button>
                </CardActions>
            </form>
        </AuthPageLayout>
    );
};

MfaVerifyPage.path = '/mfa-verify';
